/**
 * ReportGenerationContext
 * Suivi en tâche de fond des rapports en cours de génération.
 * - Toast loading persistant par rapport (visible même après changement de page)
 * - Poll /status toutes les 5s jusqu'à completed/failed
 * - Toast success ou error à la fin
 * - Callback registerOnComplete() pour rafraîchir la liste
 */

import React, { createContext, useContext, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { generatedReports } from '../services/api/client';

const ReportGenerationContext = createContext(null);

export function ReportGenerationProvider({ children }) {
  // Map reportId → { toastId, intervalId, name, type }
  const tracked = useRef(new Map());
  // Callbacks appelés quand n'importe quel rapport termine
  const onCompleteCallbacks = useRef(new Set());

  const stopTracking = useCallback((reportId) => {
    const entry = tracked.current.get(reportId);
    if (entry) {
      clearInterval(entry.intervalId);
      tracked.current.delete(reportId);
    }
  }, []);

  const trackReport = useCallback((reportId, reportName, reportType) => {
    if (!reportId || tracked.current.has(reportId)) return;

    const label = reportName || `Rapport ${reportType || ''}`.trim();

    // Toast loading persistant
    const toastId = toast.loading(`Génération en cours : ${label}…`, {
      duration: Infinity,
      style: { maxWidth: 400 },
    });

    // Poll toutes les 5s
    const intervalId = setInterval(async () => {
      try {
        const status = await generatedReports.getStatus(reportId);
        const state = status?.status;

        if (state === 'completed') {
          toast.dismiss(toastId);
          toast.success(`Rapport prêt : ${label}`, { duration: 6000 });
          stopTracking(reportId);
          onCompleteCallbacks.current.forEach(cb => { try { cb(reportId, 'completed'); } catch {} });
        } else if (state === 'failed') {
          toast.dismiss(toastId);
          toast.error(`Échec de génération : ${label}`, { duration: 8000 });
          stopTracking(reportId);
          onCompleteCallbacks.current.forEach(cb => { try { cb(reportId, 'failed'); } catch {} });
        }
        // pending / processing → continuer le poll
      } catch {
        // Erreur réseau temporaire → on continue
      }
    }, 5000);

    tracked.current.set(reportId, { toastId, intervalId, name: label, type: reportType });
  }, [stopTracking]);

  const registerOnComplete = useCallback((callback) => {
    onCompleteCallbacks.current.add(callback);
    return () => onCompleteCallbacks.current.delete(callback);
  }, []);

  return (
    <ReportGenerationContext.Provider value={{ trackReport, registerOnComplete }}>
      {children}
    </ReportGenerationContext.Provider>
  );
}

export function useReportGeneration() {
  const ctx = useContext(ReportGenerationContext);
  if (!ctx) throw new Error('useReportGeneration must be used inside ReportGenerationProvider');
  return ctx;
}

export default ReportGenerationContext;
